import {
	ChangeEventHandler,
	FC,
	useState,
	ChangeEvent,
	KeyboardEvent,
	useRef,
	useLayoutEffect,
} from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { isIOS } from 'react-device-detect';
import { Input } from 'antd';
import styles from './index.module.scss';

type CodeFieldProps = {
	name: string;
	fields: number;
	onChange?: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
	disabled: boolean;
	autoComplete?: string;
};

const MAX_LENGTH = 1;

export const CodeField: FC<CodeFieldProps> = ({ fields, onChange, disabled, autoComplete }) => {
	const [segments, setSegments] = useState(new Array(fields).fill(''));

	const inputRef = useRef(null);

	const onPaste = (event: any) => {
		const pasted = event.clipboardData?.getData('text/plain') || event.target.value;

		const pastedData = pasted?.split('').slice(0, fields);

		const newSegments = segments.map((item, i) => {
			return pastedData[i] || '';
		});

		const [fieldName, fieldIndex] = event.target.name.split('-');

		const currentSibling = document.querySelector(
			`input[name=${fieldName}-${parseInt(fieldIndex, 10)}]`,
		);

		if (currentSibling !== null && pasted.length === fields) {
			// @ts-ignore
			currentSibling.blur();
		}

		setSegments(newSegments);

		if (onChange) {
			onChange(
				// @ts-ignore
				newSegments.reduce((code, item) => {
					return `${code}${item}`;
				}, ''),
			);
		}
	};

	const changeInput = (e: ChangeEvent<HTMLInputElement>) => {
		// @ts-ignore
		if (!/[0-9]/.test(+e.target.value)) {
			return;
		}

		// @ts-ignore
		if (e.nativeEvent.inputType === 'insertFromPaste') return;

		if (e.target.value.trim().length === 6) {
			onPaste(e);
			return;
		}

		const { name, value } = e.target;

		const [fieldName, fieldIndex] = name.split('-');

		const newSegments = segments;

		if (value.length <= MAX_LENGTH) {
			// @ts-ignore
			newSegments[fieldIndex] = value;
		}

		setSegments(newSegments);

		if (value.length >= MAX_LENGTH) {
			if (parseInt(fieldIndex, 10) < 6) {
				const nextSibling = document.querySelector(
					`input[name=${fieldName}-${parseInt(fieldIndex, 10) + 1}]`,
				);

				if (nextSibling !== null) {
					// @ts-ignore
					nextSibling.focus();
				}

				if (fieldIndex === '5') {
					const lastSibling = document.querySelector(
						`input[name=${fieldName}-${parseInt(fieldIndex, 10)}]`,
					);

					if (lastSibling !== null) {
						// @ts-ignore
						lastSibling.blur();
					}
				}
			}
		}

		if (onChange) {
			onChange(
				newSegments.reduce((code, item) => {
					return `${code}${item}`;
				}, ''),
			);
		}
	};

	const onKeyDown = (e: KeyboardEvent) => {
		if (e.key === 'Backspace') {
			// @ts-ignore
			const { name, value } = e.target;
			const [fieldName, fieldIndex] = name.split('-');

			const newSegments = segments;

			newSegments[fieldIndex] = value;

			setSegments(newSegments);

			if (value.length === 0) {
				if (parseInt(fieldIndex, 10) < 6) {
					const nextSibling = document.querySelector(
						`input[name=${fieldName}-${parseInt(fieldIndex, 10) - 1}]`,
					);

					if (nextSibling !== null) {
						// @ts-ignore
						nextSibling.focus();
					}
				}
			}
		}
	};

	// usePreventScroll(isChrome && isIOS);

	useLayoutEffect(() => {
		if (inputRef && inputRef.current) {
			// @ts-ignore
			inputRef.current.focus();
		}
	}, []);

	return (
		<div className={styles.reactCodeInput}>
			{segments.map((item, i) => (
				<Input
					ref={i === 0 ? inputRef : undefined}
					key={`code-${i}`}
					name={`code-${i}`}
					value={item}
					type="text"
					onPaste={onPaste}
					maxLength={isIOS ? 6 : 1}
					onChange={changeInput}
					onKeyDown={onKeyDown}
					inputMode="numeric"
					autoComplete={autoComplete}
				/>
			))}

			{disabled && (
				<LoadingOutlined spin style={{ position: 'absolute', top: '20px', marginLeft: '5px' }} />
			)}
		</div>
	);
};
